/**
 * mw-mobile-menu.js — Alpine component for the slide-in mobile menu.
 *
 * Registered via `alpine:init` so Alpine.data('mwMenu') is wired up
 * BEFORE Alpine evaluates the `x-data="mwMenu"` on the drawer. The
 * previous `window.mwMenu = factory` pattern hit a script-load race:
 * if mw-mobile-menu.js evaluated after Alpine's first init pass,
 * Alpine bound an empty proxy and `open` ended up undefined — which
 * caused the menu to render in an inconsistent state on page load.
 *
 * ─── Stale-instance bug (fixed here) ─────────────────────────────
 * Dawn's cart-pubsub fetches and replaces the header section HTML
 * when the cart updates (component-cart-drawer.js line ~140). That
 * re-evaluates `x-data="mwMenu"` and creates a NEW Alpine instance —
 * but the OLD instance's bus listeners stayed bound, each pointing at
 * a stale `this` whose `$el` referenced the detached root. On second
 * open, both old and new openMenu() fired; on close, the old close()
 * ran first, mutating the detached DOM and clobbering nothing visible.
 * The new instance's close did not always run before the click event
 * propagated, leaving the menu open.
 *
 * Fix: operate on the LIVE DOM via querySelector inside open/close
 * helpers, not on `this.$el`. Component state (open: bool) is still
 * Alpine's so :class="{ 'is-open': open }" still drives the slide.
 * Bus listeners always target the currently mounted root element,
 * regardless of which Alpine instance is "winning" the binding race.
 *
 * Opens via the mwBus 'menu:open' event (header burger emits this).
 * Closes via 'menu:close', ESC key, scrim click, or any internal link.
 */
(function () {
  // ─── Live-DOM helpers — never touch detached old roots ─────
  function liveRoot() { return document.querySelector('.mw-menu'); }

  function syncAria(isOpen) {
    var root = liveRoot();
    if (!root) return;
    if (isOpen) {
      root.removeAttribute('inert');
      root.setAttribute('aria-hidden', 'false');
      root.classList.add('is-open');
    } else {
      root.setAttribute('inert', '');
      root.setAttribute('aria-hidden', 'true');
      root.classList.remove('is-open');
    }
  }

  // Public close + open — anyone can call these regardless of Alpine
  // (inline onclick, console, devtools) and they always operate on the
  // currently mounted drawer.
  window.mwCloseMobileMenu = function () {
    document.body.style.overflow = '';
    syncAria(false);
    // Tell Alpine the state — find the live Alpine component instance
    // bound to the current root and flip `open`. Use Alpine.$data so
    // we get the proxy, not a stale closure reference.
    var root = liveRoot();
    if (root && window.Alpine && window.Alpine.$data) {
      var d = window.Alpine.$data(root);
      if (d) d.open = false;
    }
    if (window.mwBus) window.mwBus.emit('menu:closed', {});
  };

  window.mwOpenMobileMenu = function () {
    document.body.style.overflow = 'hidden';
    syncAria(true);
    var root = liveRoot();
    if (root && window.Alpine && window.Alpine.$data) {
      var d = window.Alpine.$data(root);
      if (d) d.open = true;
    }
    if (root) {
      var btn = root.querySelector('.mw-menu__close');
      if (btn) btn.focus();
    }
  };

  // Bus listeners — bound ONCE on the window, NOT per-Alpine-init,
  // so a header re-render won't accumulate duplicates or strand them
  // on detached instances. Guarded by window._mwMenuBusBound.
  function bindBusOnce() {
    if (window._mwMenuBusBound) return;
    if (!window.mwBus) { window.setTimeout(bindBusOnce, 50); return; }
    window.mwBus.on('menu:open',  function () { window.mwOpenMobileMenu(); });
    window.mwBus.on('menu:close', function () { window.mwCloseMobileMenu(); });
    window._mwMenuBusBound = true;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindBusOnce);
  } else {
    bindBusOnce();
  }

  // ─── Alpine component — slim, just owns the boolean state ─────
  document.addEventListener('alpine:init', function () {
    if (!window.Alpine) return;
    window.Alpine.data('mwMenu', function () {
      return {
        open: false,
        close: function () { window.mwCloseMobileMenu(); },
        init: function () {
          // Sync DOM to initial state once. No bus listeners here —
          // those live globally so a re-init doesn't accumulate them.
          syncAria(false);
        }
      };
    });
  });
})();
